import {
  BlurMask, Canvas, Group, Path, Skia,
  Text as SkiaText,
  useFont,
  Shadow,
} from '@shopify/react-native-skia';
import {
  Dimensions, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import Animated, {
  SlideInRight, SlideOutRight, useDerivedValue, useSharedValue, withTiming,
} from 'react-native-reanimated';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import BuildingSelector from '../BuildingSelector';
import PressableStyled from '../PressableStyled';
import DownArrow24 from '../24pxIcons/DownArrow24';
import { HappyScoreCategory } from '../../constants/Types';
import TileSelector, { TileDataItem } from './TileSelector';
import { happyScoreMetrics } from '../../graphs/HappyDetailsGraphWeb';
import HomeInsightsMobile from '../HomeInsightsMobile';
import HappyDetailsMobile from './HappyDetailsMobile';
import { DateRange } from '../../redux/dateRangeSlice';
import GetMetricHappyScoreData from '../../requests/Sensors/GetMetricHappyScoreData';

const HappyHomeMobile = ({ name, isDev }) => {
  // eslint-disable-next-line global-require
  const fontType = useFont(require('../../assets/fonts/Poppins-SemiBold.ttf'), 78);

  const hi = `Hi, ${name}`;

  const { width } = Dimensions.get('window');

  const r = Math.min((width - 150), 250) / 2;

  const path = Skia.Path.Make();
  const outerPath = Skia.Path.Make();

  const target = useSharedValue(0);

  const [selectedTileIndex, setSelectedTileIndex] = useState(0);
  const [graphOpen, setGraphOpen] = useState(false);

  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);

  const { start, end }: DateRange = useSelector((state: { dateRange: DateRange }) => state
    .dateRange);

  const [tileData, setTileData] = useState<TileDataItem[]>([
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 0,
      lineShown: true,
      screen: HappyScoreCategory.HAPPY_SCORE,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 1,
      lineShown: false,
      screen: HappyScoreCategory.INTERNAL_ENVIRONMENT,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 2,
      lineShown: false,
      screen: HappyScoreCategory.EXTERNAL_ENVIRONMENT,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 3,
      lineShown: false,
      screen: HappyScoreCategory.IT,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 4,
      lineShown: false,
      screen: HappyScoreCategory.ENERGY,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 5,
      lineShown: false,
      screen: HappyScoreCategory.OCCUPANCY,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 6,
      lineShown: false,
      screen: HappyScoreCategory.AMENITIES,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 7,
      lineShown: false,
      screen: HappyScoreCategory.ERGONOMICS,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 8,
      lineShown: false,
      screen: HappyScoreCategory.SATISFACTION,
    },
  ]);

  const [graphData, setGraphData] = useState(tileData.map(() => []));

  useEffect(() => {
    updateGraphData();
  }, [buildingId, start, end]);

  const updateGraphData = () => {
    const newGraphData: object[][] = [[], [], [], [], [], [], [], [], []];
    setGraphData(newGraphData);
    happyScoreMetrics.forEach(({ metric }, metricIndex) => {
      GetMetricHappyScoreData({ metric, range: { start, end } })?.then((data) => {
        if (!data || !Array.isArray(data)) return;
        const values = data.map((point) => point?.value).filter((v) => v !== undefined);
        const average = values.length > 0
          ? values.reduce((prev, next) => prev + next, 0) / values.length : null;
        setTileData((prev) => {
          const updated = [...prev];
          updated[metricIndex] = {
            ...updated[metricIndex],
            average: Number.isFinite(average) ? average : null,
          };
          return updated;
        });
        if (values.length > 0) {
          const newScore = values[values.length - 1];
          setTileData((prev) => {
            const updated = [...prev];
            updated[metricIndex] = {
              ...updated[metricIndex],
              score: Number.isFinite(newScore) ? Math.round(newScore) : null,
            };
            return updated;
          });
          if (metricIndex === 0 && target?.value !== undefined) {
            target.value = withTiming(newScore / 100, { duration: 1000 });
          }
        }
        setGraphData((prev) => {
          const updatedGraph = [...prev];
          updatedGraph[metricIndex] = data.map(({ value, date }, index) => ({
            value,
            date,
            index,
          }));
          return updatedGraph;
        });
      });
    });
  };

  const color = useDerivedValue(() => {
    if (target.value < 0.5) return Colors.light.hereworksPink;
    if (target.value < 0.8) return Colors.light.hereworksOrange;
    return Colors.light.hereworksTeal;
  }, [target.value]);

  const textColor = useDerivedValue(() => {
    if (target.value < 0.5) return Colors.light.hereworksPink;
    if (target.value < 0.8) return Colors.light.hereworksOrange;
    return Colors.light.hereworksExtraDarkTeal;
  }, [target.value]);

  const happyScoreText = useDerivedValue(() => `${Math.round((target.value ?? 0) * 100)}`, [target.value]);

  const circleY = 160;

  const drawCircle = () => {
    path.addCircle(width / 2, circleY, r);
    outerPath.addCircle(width / 2, circleY, r + 10);
    const m = Skia.Matrix();
    m.translate(width / 2, circleY);
    m.rotate(-Math.PI / 2);
    m.translate(-(width / 2), -circleY);
    path.transform(m);
  };

  drawCircle();

  useEffect(() => {
    target.value = withTiming(tileData[selectedTileIndex].score / 100, { duration: 1000 });
  }, [selectedTileIndex]);

  const textX = useDerivedValue(() => {
    if (!fontType) return -100;
    try {
      const textPos = fontType?.measureText(happyScoreText?.value ?? '0');
      return (width / 2 - (textPos.width / 2));
    } catch {
      if (happyScoreText.value === '100') return (width / 2 - 64);
      if (happyScoreText.value === '0') return (width / 2 - 26);
      return (width / 2 - 44);
    }
  }, [happyScoreText.value, fontType]);

  return (
    <>
      <ScrollView
        style={{ flex: 1, backgroundColor: Colors.light.hereworksBackground }}
      >
        <BuildingSelector testID='home-screen-building-selector' />
        <Canvas style={{ height: 320 }}>
          <Path
            path={outerPath}
            start={0}
            end={1}
            color={'#fff'}
            style={'stroke'}
            strokeWidth={24}
            strokeCap={'round'}
          />
          <Shadow dx={0} dy={1} blur={2} color={'#00000030'} />
          <Group>
            <Path
              path={path}
              start={0}
              end={1}
              color={'#fff'}
              style={'stroke'}
              strokeWidth={22}
              strokeCap={'round'}
            />
            {/* <BlurMask blur={10} style="inner" /> */}
            <Shadow dx={10} dy={10} blur={5} color={'#00000020'} inner />
            <Shadow dx={0} dy={2} blur={5} color={'#00000030'} inner />
          </Group>
          <Group>
            <Path
              path={path}
              start={0}
              end={target}
              color={'#fff'}
              style={'stroke'}
              strokeWidth={14}
              strokeCap={'round'}
            />
            <BlurMask blur={20} style="inner" />
          </Group>
          <Group>
            <Path
              path={path}
              start={0}
              end={target}
              color={color}
              style={'stroke'}
              strokeWidth={14}
              strokeCap={'round'}
            />
            {/* <BlurMask blur={15} style="normal" /> */}
            <Shadow dx={0} dy={5} blur={10} color={color} />
          </Group>
          <Path
            path={path}
            start={0}
            end={target}
            color={color}
            style={'stroke'}
            strokeWidth={16}
            strokeCap={'round'}
          />
          <SkiaText
            x={textX}
            y={180}
            text={happyScoreText}
            font={fontType}
            color={textColor}
          />
        </Canvas>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              fontSize: 34,
              fontFamily: 'Poppins_600SemiBold',
              alignSelf: 'stretch',
              textAlign: 'center',
            }}
          >
            {hi}
          </Text>
          <Text
            style={{
              fontSize: 16,
              fontFamily: 'Poppins_500Medium',
              alignSelf: 'stretch',
              textAlign: 'center',
            }}
          >
            We're in a happy place today
          </Text>
          <PressableStyled
            style={{
              alignSelf: 'center',
              marginTop: 20,
              flexDirection: 'row',
              alignItems: 'center',
            }}
            onPress={() => setGraphOpen(true)}
          >
            <Text style={{ color: Colors.light.hereworksBlue500 }}>
              View graph
            </Text>
            <DownArrow24 style={{ transform: [{ rotate: '-90deg' }] }} />
          </PressableStyled>
          <View
            style={{
              borderBottomWidth: 1,
              borderColor: Colors.light.hereworksBlue100,
              marginHorizontal: 24,
            }}
          >
            <TileSelector
              data={tileData}
              selectedIndex={selectedTileIndex}
              setSelectedIndex={setSelectedTileIndex}
            />
          </View>
          <View style={{ padding: 24 }}>
            <Text style={{ fontSize: 22, fontFamily: 'Poppins_400Regular' }}>
              Insights
            </Text>
            <HomeInsightsMobile
              dateRange={{
                start,
                end,
              }}
              {...tileData[selectedTileIndex]}
            />
          </View>
        </View>
      </ScrollView>
      {graphOpen ? (
        <Animated.View
          style={StyleSheet.absoluteFill}
          entering={SlideInRight}
          exiting={SlideOutRight}
        >
          <HappyDetailsMobile
            goBack={() => setGraphOpen(false)}
            score={tileData[0].score}
            {...{
              tileData,
              setTileData,
              graphData,
              isDev,
            }}
          />
        </Animated.View>
      ) : null}
    </>
  );
};

export default HappyHomeMobile;
